import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const BRAND = {
  primary: [47, 61, 142],      // #2F3D8E
  secondary: [76, 140, 59],    // #4C8C3B
  accent: [93, 165, 216],      // #5DA5D8
  gray: [120, 120, 120],
  lightGray: [240, 240, 240],
  growth: [76, 140, 59],
  loss: [220, 70, 70],
};

const ANALYSIS_LABELS = {
  producto: 'Productos',
  laboratorio: 'Laboratorios',
  clase_terapeutica: 'Clases Terapéuticas',
  grupo_canal: 'Canales de Distribución',
  provincia: 'Provincias',
};

// Sanitize text for jsPDF (default helvetica is WinAnsi - fix Unicode chars)
function sanitize(text) {
  if (!text) return '';
  return String(text)
    .replace(/\*\*/g, '')   // Remove markdown bold
    .replace(/__/g, '')      // Remove markdown italic
    .replace(/[\u2013\u2014]/g, '-')   // En/em dash -> hyphen
    .replace(/[\u2018\u2019]/g, "'")   // Smart single quotes
    .replace(/[\u201C\u201D]/g, '"')   // Smart double quotes
    .replace(/\u2026/g, '...')         // Ellipsis
    .replace(/[\u2022\u25CF]/g, '-')   // Bullets
    .replace(/\u00A0/g, ' ')           // Non-breaking space
    .replace(/[\u200B-\u200D\uFEFF]/g, ''); // Zero-width chars
}

async function loadLogo() {
  try {
    const response = await fetch('/logo_analytica.png');
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function drawHeader(doc, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('ANALYTICA', 14, 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(sanitize(title), pageWidth / 2, 9, { align: 'center' });
}

function drawFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND.lightGray);
  doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.gray);
  doc.setFont('helvetica', 'normal');
  doc.text('Analytica - Analisis de Elasticidad de Precios', 14, pageHeight - 6);
  doc.text(new Date().toLocaleDateString('es-EC'), pageWidth - 14, pageHeight - 6, { align: 'right' });
}

function parseRecommendation(text) {
  if (!text) return [];
  const lines = sanitize(text).split('\n').map(l => l.trim()).filter(l => l);
  const items = [];
  let current = null;
  const numberedRe = /^\s*(\d+)[.)]\s*(.+?)[:.]?\s*$/;
  const bulletRe = /^\s*[-]\s*(.+)$/;

  for (const line of lines) {
    const num = line.match(numberedRe);
    const bullet = line.match(bulletRe);
    if (num) {
      if (current) items.push(current);
      current = { title: num[2].trim(), body: '' };
    } else if (bullet) {
      const txt = bullet[1].trim();
      const splitMatch = txt.match(/^([^:.]+[:.])\s*(.*)$/);
      if (splitMatch && splitMatch[1].length < 120) {
        if (current) items.push(current);
        current = { title: splitMatch[1].replace(/[:.\s]+$/, '').trim(), body: splitMatch[2] || '' };
      } else {
        if (current) items.push(current);
        current = { title: txt.slice(0, 80), body: txt.length > 80 ? txt : '' };
      }
    } else {
      if (current) {
        current.body += (current.body ? '\n' : '') + line;
      } else {
        items.push({ title: '', body: line });
      }
    }
  }
  if (current) items.push(current);
  return items.length ? items : [{ title: '', body: text }];
}

function fmtCurrency(n) {
  if (n == null) return '-';
  return '$' + Number(n).toLocaleString('es-EC', { maximumFractionDigits: 0 });
}

function fmtNumber(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('es-EC', { maximumFractionDigits: 0 });
}

function fmtGrowth(n) {
  if (n == null) return 'N/A';
  return (n > 0 ? '+' : '') + n.toFixed(1) + '%';
}

// ============ AI RECOMMENDATION PDF ============
export async function generateAIRecommendationPDF({ analysisType, recommendation, productsAnalyzed, filters, objectives }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const logo = await loadLogo();

  // ---- COVER ----
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, 80, 'F');
  if (logo) {
    try { doc.addImage(logo, 'PNG', pageWidth / 2 - 20, 18, 40, 40); } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Recomendaciones Estrategicas', pageWidth / 2, 70, { align: 'center' });

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(sanitize(`Analisis por ${ANALYSIS_LABELS[analysisType] || analysisType}`),
    pageWidth / 2, 100, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(...BRAND.gray);
  doc.text(`Generado: ${new Date().toLocaleString('es-EC')}`, pageWidth / 2, 112, { align: 'center' });
  doc.text(`${productsAnalyzed} elementos analizados`, pageWidth / 2, 119, { align: 'center' });
  if (filters && Object.values(filters).some(v => v)) {
    let filterText = 'Filtros: ';
    Object.entries(filters).forEach(([k, v]) => { if (v) filterText += `${k}=${v}; `; });
    doc.text(sanitize(filterText), pageWidth / 2, 126, { align: 'center' });
  }

  // Methodology box
  doc.setFillColor(247, 250, 252);
  doc.roundedRect(20, 145, pageWidth - 40, 70, 3, 3, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.primary);
  doc.text('Metodologia', 26, 156);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(50, 50, 50);
  const methodology = doc.splitTextToSize(
    sanitize(
      'Este informe analiza la sensibilidad al precio de cada producto sobre los datos de mercado. ' +
      'Se considera "sensible" aquel producto cuyo volumen responde de forma consistente y ' +
      'estadisticamente significativa a los cambios de precio (β negativo, p<0.05, ' +
      'estabilidad confirmada). Las recomendaciones se agrupan en objetivos estrategicos para ' +
      'facilitar la ejecucion por parte de los equipos comerciales, marketing y gerencia.'
    ),
    pageWidth - 52
  );
  doc.text(methodology, 26, 164);

  drawFooter(doc);

  // ---- SMARTART OBJECTIVES ----
  if (objectives && objectives.length > 0) {
    const objColors = [
      [52, 152, 219],   // blue
      [76, 140, 59],    // green
      [231, 126, 34],   // orange
      [155, 89, 182],   // purple
      [52, 73, 94],     // dark blue
    ];

    objectives.forEach((obj, idx) => {
      doc.addPage();
      drawHeader(doc, `Objetivo ${idx + 1}: ${obj.title || ''}`);

      const color = objColors[idx % objColors.length];

      // Big header card for the objective
      doc.setFillColor(...color);
      doc.roundedRect(14, 22, pageWidth - 28, 30, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`OBJETIVO ${idx + 1}`, 20, 30);
      doc.setFontSize(16);
      doc.text(sanitize(obj.title || ''), 20, 38);
      if (obj.summary) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const sumLines = doc.splitTextToSize(sanitize(obj.summary), pageWidth - 40);
        doc.text(sumLines.slice(0, 1), 20, 46);
      }

      // Recommendations
      let y = 62;
      (obj.recommendations || []).forEach((rec, rIdx) => {
        if (y > 255) {
          drawFooter(doc);
          doc.addPage();
          drawHeader(doc, `Objetivo ${idx + 1}: ${obj.title || ''} (cont.)`);
          y = 22;
        }

        // Numbered circle
        doc.setFillColor(...color);
        doc.circle(22, y + 4, 5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(`${idx + 1}.${rIdx + 1}`, 22, y + 5.5, { align: 'center' });

        // Title + impact
        doc.setTextColor(...BRAND.primary);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        const actionLines = doc.splitTextToSize(sanitize(rec.action || ''), pageWidth - 60);
        doc.text(actionLines, 32, y + 4);

        if (rec.impact) {
          const impactColor = rec.impact === 'Alto' ? [220, 53, 69] :
                             rec.impact === 'Medio' ? [255, 193, 7] : [108, 117, 125];
          doc.setFillColor(...impactColor);
          doc.roundedRect(pageWidth - 40, y, 26, 7, 1, 1, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text(sanitize(rec.impact), pageWidth - 27, y + 4.5, { align: 'center' });
        }

        y += actionLines.length * 5 + 2;

        // Detail
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const detailLines = doc.splitTextToSize(sanitize(rec.detail || ''), pageWidth - 46);
        doc.text(detailLines, 32, y + 3);
        y += detailLines.length * 4.5 + 6;
      });

      drawFooter(doc);
    });
  } else {
    // Fallback - plain parsed text
    doc.addPage();
    drawHeader(doc, 'Recomendaciones Estrategicas');
    let y = 22;
    const items = parseRecommendation(recommendation);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...BRAND.primary);
    doc.text('Recomendaciones', 14, y);
    y += 8;
    doc.setDrawColor(...BRAND.secondary);
    doc.line(14, y - 3, pageWidth - 14, y - 3);
    items.forEach((item, idx) => {
      if (y > 260) {
        drawFooter(doc);
        doc.addPage();
        drawHeader(doc, 'Recomendaciones Estrategicas');
        y = 22;
      }
      if (item.title) {
        doc.setFillColor(...BRAND.primary);
        doc.circle(20, y + 2, 4, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(String(idx + 1), 20, y + 3.5, { align: 'center' });
        doc.setTextColor(...BRAND.primary);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        const titleLines = doc.splitTextToSize(sanitize(item.title), pageWidth - 38);
        doc.text(titleLines, 28, y + 3);
        y += titleLines.length * 5 + 2;
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      const bodyLines = doc.splitTextToSize(sanitize(item.body || ''), pageWidth - 38);
      doc.text(bodyLines, 28, y + 4);
      y += bodyLines.length * 4.5 + 8;
    });
    drawFooter(doc);
  }

  const filename = `recomendaciones_${analysisType}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

// ============ ITEM-LEVEL PDF (single product/lab/etc) ============
export async function generateItemRecommendationPDF({ itemType, itemName, recommendation, history, elasticities }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const logo = await loadLogo();

  // Header
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');
  if (logo) {
    try { doc.addImage(logo, 'PNG', 14, 6, 22, 22); } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Recomendacion', 42, 16);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(sanitize(`${ANALYSIS_LABELS[itemType] || itemType}: ${itemName}`), 42, 25);
  doc.setFontSize(8);
  doc.text(new Date().toLocaleString('es-EC'), pageWidth - 14, 25, { align: 'right' });

  let y = 50;
  // History summary
  if (history && history.length) {
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Historico', 14, y);
    y += 6;
    autoTable(doc, {
      head: [['Mes', 'Valores ($)', 'Unidades']],
      body: history.slice(-12).map(h => [
        sanitize(h.mes),
        fmtCurrency(h.valores),
        fmtNumber(h.unidades),
      ]),
      startY: y,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Elasticities
  if (elasticities && elasticities.length) {
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Sensibilidad al Precio', 14, y);
    y += 6;
    autoTable(doc, {
      head: [['Clasificacion', '|β|', 'p-value', 'R²', 'Meses', 'Motivo']],
      body: elasticities.slice(0, 5).map(e => [
        sanitize(e.classification || (e.cluster || '-')),
        e.elasticidad_abs != null ? Number(e.elasticidad_abs).toFixed(3) :
          (e.elasticidad != null ? Math.abs(e.elasticidad).toFixed(3) : '-'),
        e.pvalue != null ? Number(e.pvalue).toFixed(3) : '-',
        e.r_squared != null ? Number(e.r_squared).toFixed(2) : '-',
        e.n_months || '-',
        sanitize(e.classification === 'Sensible' ? 'OK' : (e.reason || '-').replace(/_/g, ' ')),
      ]),
      startY: y,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Recommendations
  doc.setTextColor(...BRAND.primary);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Recomendaciones', 14, y);
  y += 6;

  const items = parseRecommendation(recommendation);
  items.forEach((item, idx) => {
    if (y > 260) {
      drawFooter(doc);
      doc.addPage();
      drawHeader(doc, sanitize(`Recomendacion - ${itemName}`));
      y = 22;
    }
    if (item.title) {
      doc.setFillColor(...BRAND.primary);
      doc.circle(20, y + 2, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(String(idx + 1), 20, y + 3.5, { align: 'center' });
      doc.setTextColor(...BRAND.primary);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(sanitize(item.title), pageWidth - 38);
      doc.text(titleLines, 28, y + 3);
      y += titleLines.length * 5 + 2;
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    const bodyLines = doc.splitTextToSize(sanitize(item.body || ''), pageWidth - 38);
    doc.text(bodyLines, 28, y + 4);
    y += bodyLines.length * 4.5 + 8;
  });

  drawFooter(doc);
  const safeName = itemName.replace(/[^a-z0-9]/gi, '_').slice(0, 40);
  const filename = `recomendacion_${itemType}_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  return filename;
}

// ============ FULL MARKET REPORT PDF ============
export async function generateMarketReportPDF({
  stats, filters,
  topData,           // { provincia, laboratorio, clase_terapeutica, producto }
  topSensitivity,    // top10 by |elasticity|
  treeClasses,       // [{nombre, growth_pct, children:[{nombre, growth_pct}]}]
  chartAnalyses,     // { sectionName: 'analysis text' }
  lastMonthYear,
  chartElements,
}) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const logo = await loadLogo();

  // ===== COVER =====
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  if (logo) {
    try { doc.addImage(logo, 'PNG', pageWidth / 2 - 30, 60, 60, 60); } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.text('Informe de Mercado', pageWidth / 2, 145, { align: 'center' });
  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  if (lastMonthYear) {
    doc.text(sanitize(`Cierre ${lastMonthYear.mes} ${lastMonthYear.año}`),
      pageWidth / 2, 158, { align: 'center' });
  }
  doc.setFontSize(11);
  doc.setTextColor(220, 230, 250);
  doc.text('Analisis de Elasticidad de Precios', pageWidth / 2, 175, { align: 'center' });
  doc.setFontSize(9);
  doc.text(`Generado: ${new Date().toLocaleString('es-EC')}`,
    pageWidth / 2, pageHeight - 20, { align: 'center' });

  // ===== EXECUTIVE SUMMARY =====
  doc.addPage();
  drawHeader(doc, 'Resumen Ejecutivo');

  doc.setTextColor(...BRAND.primary);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Resumen Ejecutivo', 14, 28);
  doc.setDrawColor(...BRAND.secondary);
  doc.setLineWidth(0.6);
  doc.line(14, 30, 70, 30);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);
  const methodIntro = doc.splitTextToSize(
    sanitize(
      'Este reporte mide la sensibilidad al precio de cada producto a partir de su historia ' +
      'mensual de ventas. Un producto es "Sensible" cuando su volumen responde de forma clara y ' +
      'consistente a los cambios de precio. Cuanto mayor el indicador |β|, mas reacciona el ' +
      'volumen ante un cambio de precio. La banda de confianza (R²) indica que tan claro es ' +
      'ese patron: alta = relacion muy consistente, baja = patron mas debil. Conocer este ' +
      'indicador ayuda a decidir cuando ajustar precios sin perder volumen, y cuando bajarlos ' +
      'para captar mas mercado.'
    ),
    pageWidth - 28
  );
  doc.text(methodIntro, 14, 38);

  // Stats grid (new schema)
  const yStats = 64;
  const cardW = (pageWidth - 28 - 12) / 5;
  const bands = stats?.confidence_bands || {};
  const sensTotal = stats?.sensibles ?? ((bands.alta || 0) + (bands.media || 0) + (bands.baja || 0));
  const noSensTotal = stats?.no_sensibles ?? (stats?.clusters?.no_sensible || 0);
  const statCards = [
    { label: 'Filas', value: fmtNumber(stats?.total_rows || 0) },
    { label: 'Productos', value: fmtNumber(sensTotal + noSensTotal) },
    { label: 'Sensibles', value: fmtNumber(sensTotal), color: BRAND.secondary },
    { label: 'No Sensibles', value: fmtNumber(noSensTotal), color: [148, 163, 184] },
    { label: '|β| Prom.', value: Math.abs(stats?.avg_elasticity || 0).toFixed(2) },
  ];
  statCards.forEach((c, i) => {
    const x = 14 + i * (cardW + 3);
    doc.setFillColor(...(c.color || BRAND.primary));
    doc.roundedRect(x, yStats, cardW, 22, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(sanitize(c.label), x + 3, yStats + 6);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(String(c.value), x + 3, yStats + 17);
  });

  drawFooter(doc);

  // ===== CHARTS PAGES with ANALYSIS =====
  if (chartElements) {
    for (const [section, el] of Object.entries(chartElements)) {
      if (!el) continue;
      try {
        const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 1.5, logging: false });
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        doc.addPage();
        drawHeader(doc, sanitize(section));
        doc.setTextColor(...BRAND.primary);
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.text(sanitize(section), 14, 28);
        doc.setDrawColor(...BRAND.secondary);
        doc.line(14, 30, 70, 30);

        const imgW = pageWidth - 28;
        const imgH = (canvas.height * imgW) / canvas.width;
        const finalH = Math.min(imgH, pageHeight - 100);
        doc.addImage(imgData, 'JPEG', 14, 35, imgW, finalH);

        // Chart analysis box below
        const yAnalysis = 35 + finalH + 6;
        if (yAnalysis < pageHeight - 30) {
          const analysisText = chartAnalyses?.[section];
          if (analysisText) {
            doc.setFillColor(247, 250, 252);
            const boxH = Math.min(38, pageHeight - 30 - yAnalysis);
            doc.roundedRect(14, yAnalysis, pageWidth - 28, boxH, 2, 2, 'F');
            doc.setTextColor(...BRAND.primary);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text('Analisis del grafico', 18, yAnalysis + 6);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(50, 50, 50);
            const lines = doc.splitTextToSize(sanitize(analysisText), pageWidth - 36);
            doc.text(lines.slice(0, 6), 18, yAnalysis + 12);
          }
        }
        drawFooter(doc);
      } catch (e) {
        console.warn('Chart capture failed for', section, e);
      }
    }
  }

  // ===== TOP TABLES (with growth %) =====
  const sectionMap = {
    provincia: { title: 'Top 10 Provincias' },
    laboratorio: { title: 'Top 10 Laboratorios' },
    clase_terapeutica: { title: 'Top 10 Clases Terapeuticas' },
  };

  for (const [section, cfg] of Object.entries(sectionMap)) {
    const data = (topData && topData[section]) || [];
    if (!data.length) continue;

    doc.addPage();
    drawHeader(doc, cfg.title);
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text(cfg.title, 14, 28);
    doc.setDrawColor(...BRAND.secondary);
    doc.line(14, 30, 70, 30);

    autoTable(doc, {
      head: [['#', cfg.title.replace('Top 10 ', ''), 'Valores ($)', 'Unidades', 'Crecimiento %']],
      body: data.slice(0, 10).map((d, i) => [
        i + 1,
        sanitize((d.nombre || '-').substring(0, 45)),
        fmtCurrency(d.valores),
        fmtNumber(d.unidades),
        fmtGrowth(d.growth_pct),
      ]),
      startY: 35,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { fontSize: 9, textColor: 50 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 4) {
          const txt = hookData.cell.text[0] || '';
          if (txt.startsWith('+')) hookData.cell.styles.textColor = BRAND.growth;
          else if (txt.startsWith('-') && txt !== 'N/A') hookData.cell.styles.textColor = BRAND.loss;
        }
      },
      margin: { left: 14, right: 14 },
    });
    drawFooter(doc);
  }

  // ===== TOP 10 PRODUCTS BY VALUE WITH GROWTH =====
  const products = (topData && topData.producto) || [];
  if (products.length) {
    doc.addPage();
    drawHeader(doc, 'Top 10 Productos por Valor');
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Top 10 Productos por Valor', 14, 28);
    doc.setDrawColor(...BRAND.secondary);
    doc.line(14, 30, 70, 30);

    autoTable(doc, {
      head: [['#', 'Producto', 'Laboratorio', 'Valores ($)', 'Crecimiento %']],
      body: products.slice(0, 10).map((p, i) => [
        i + 1,
        sanitize((p.nombre || '-').substring(0, 40)),
        sanitize((p.laboratorio || '-').substring(0, 22)),
        fmtCurrency(p.valores),
        fmtGrowth(p.growth_pct),
      ]),
      startY: 35,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 10 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 4) {
          const txt = hookData.cell.text[0] || '';
          if (txt.startsWith('+')) hookData.cell.styles.textColor = BRAND.growth;
          else if (txt.startsWith('-') && txt !== 'N/A') hookData.cell.styles.textColor = BRAND.loss;
        }
      },
      margin: { left: 14, right: 14 },
    });
    drawFooter(doc);
  }

  // ===== TOP 10 PRODUCTS BY SENSITIVITY =====
  if (topSensitivity && topSensitivity.length) {
    doc.addPage();
    drawHeader(doc, 'Top 10 Productos por Sensibilidad');
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Top 10 Productos por Sensibilidad', 14, 28);
    doc.setDrawColor(...BRAND.secondary);
    doc.line(14, 30, 70, 30);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(sanitize(
      'Productos con mayor |β| (sensibilidad al precio) y patron estadisticamente significativo. ' +
      'Su volumen responde de forma consistente a los cambios de precio. ' +
      'Recomendacion: gestionar el precio con cuidado para maximizar volumen y rentabilidad.'
    ), 14, 36, { maxWidth: pageWidth - 28 });

    autoTable(doc, {
      head: [['#', 'Producto', 'Laboratorio', '|β|', 'p-value', 'R²', 'Confianza']],
      body: topSensitivity.slice(0, 10).map((p, i) => [
        i + 1,
        sanitize((p.Producto || '-').substring(0, 38)),
        sanitize((p.Laboratorio || '-').substring(0, 18)),
        p.elasticidad_abs != null ? Number(p.elasticidad_abs).toFixed(3) :
          (p.elasticidad != null ? Math.abs(p.elasticidad).toFixed(3) : '-'),
        p.pvalue != null ? Number(p.pvalue).toFixed(3) : '-',
        p.r_squared != null ? Number(p.r_squared).toFixed(2) : '-',
        sanitize((p.confidence_band || '-').toUpperCase()),
      ]),
      startY: 50,
      theme: 'striped',
      headStyles: { fillColor: BRAND.primary, textColor: 255, fontSize: 10 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });
    drawFooter(doc);
  }

  // ===== TREE: TOP 3 CLASE I -> CLASE II GROWTH =====
  if (treeClasses && treeClasses.length) {
    doc.addPage();
    drawHeader(doc, 'Crecimiento por Clase Terapeutica - Arbol');
    doc.setTextColor(...BRAND.primary);
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Top Crecimiento - Arbol Clase I / II', 14, 28);
    doc.setDrawColor(...BRAND.secondary);
    doc.line(14, 30, 70, 30);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(sanitize(
      'Las 3 mejores Clases Terapeuticas (nivel I) por crecimiento mensual, desglosadas en sus ' +
      'subclases (nivel II) tambien por crecimiento.'
    ), 14, 36, { maxWidth: pageWidth - 28 });

    let y = 50;
    treeClasses.forEach((c1, idx) => {
      if (y > 250) {
        drawFooter(doc);
        doc.addPage();
        drawHeader(doc, 'Crecimiento por Clase Terapeutica - Arbol');
        y = 22;
      }

      // Parent box
      doc.setFillColor(...BRAND.primary);
      doc.roundedRect(14, y, pageWidth - 28, 10, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(sanitize(`${idx + 1}. ${c1.nombre}`), 18, y + 6.5);
      doc.text(fmtGrowth(c1.growth_pct), pageWidth - 18, y + 6.5, { align: 'right' });
      y += 13;

      // Children
      if (c1.children && c1.children.length) {
        const childRows = c1.children.map(c2 => [
          sanitize((c2.nombre || '-').substring(0, 40)),
          fmtCurrency(c2.previous),
          fmtCurrency(c2.current),
          fmtGrowth(c2.growth_pct),
        ]);
        autoTable(doc, {
          head: [['Subclase II', 'Mes Anterior', 'Mes Actual', 'Crecimiento %']],
          body: childRows,
          startY: y,
          theme: 'plain',
          headStyles: { fillColor: [240, 245, 252], textColor: BRAND.primary, fontSize: 9, fontStyle: 'bold' },
          bodyStyles: { fontSize: 9, textColor: 50 },
          alternateRowStyles: { fillColor: [252, 252, 252] },
          margin: { left: 22, right: 14 },
          didParseCell: (hookData) => {
            if (hookData.section === 'body' && hookData.column.index === 3) {
              const txt = hookData.cell.text[0] || '';
              if (txt.startsWith('+')) hookData.cell.styles.textColor = BRAND.growth;
              else if (txt.startsWith('-') && txt !== 'N/A') hookData.cell.styles.textColor = BRAND.loss;
            }
          },
        });
        y = doc.lastAutoTable.finalY + 6;
      }
    });
    drawFooter(doc);
  }

  // Save
  const monthLabel = lastMonthYear
    ? `${lastMonthYear.año}_${lastMonthYear.mes}`.toLowerCase()
    : new Date().toISOString().slice(0, 7);
  const filename = `informe_mercado_${monthLabel}.pdf`;
  doc.save(filename);
  return filename;
}
